import { authApi } from "@/core/auth/services/auth.api"
import { extractTokenPayload, getUserTypeFromAuthResponse } from "@/core/auth/utils/token.utils"
import { isAdminLoginAllowed } from "@/constants/auth"
import { clearCurrentUser, fetchCurrentUser, setUserFromToken, useStore } from "@/store"
import { getToken, isTokenExpired, removeTokens } from "@/utils/auth"
import AppRoutes from "@/routes/index"
import React from "react"
import { Toaster } from "sonner"
import { HelmetProvider } from "react-helmet-async"
import ErrorBoundary from "@/core/error-pages/ErrorBoundary"
import { useEffect, useState } from "react"

import "@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css"
import "react-calendar/dist/Calendar.css"
import "react-clock/dist/Clock.css"
import "react-datetime-picker/dist/DateTimePicker.css"

const App: React.FC = () => {
  const [authReady, setAuthReady] = useState(false)
  const currentUserId = useStore((state) => state.currentUser?.id)

  useEffect(() => {
    let cancelled = false

    const bootstrapAuth = async () => {
      const token = getToken()
      const isLoginPage = window.location.pathname.startsWith("/login")

      if (isLoginPage && !token) {
        clearCurrentUser()
        if (!cancelled) setAuthReady(true)
        return
      }

      const shouldTryRefresh = !token || isTokenExpired(token) || !currentUserId

      if (!shouldTryRefresh) {
        // Persisted session geçerliyse bile permission'ları backend'den tazele.
        // Rol/yetki değişiklikleri localStorage'daki eski state ile kalmasın.
        fetchCurrentUser(true).catch(() => {
          // Koruyucu: auth boot sürecini bloklama.
        })
        if (!cancelled) setAuthReady(true)
        return
      }

      try {
        const response = await authApi.refresh()
        const restoredPayload = extractTokenPayload(response)
        const userType = getUserTypeFromAuthResponse(restoredPayload)
        const isStaffSession =
          userType != null ? isAdminLoginAllowed(userType) : false

        setUserFromToken(restoredPayload, isStaffSession)
      } catch {
        removeTokens()
        clearCurrentUser()
      } finally {
        if (!cancelled) setAuthReady(true)
      }
    }

    void bootstrapAuth()

    return () => {
      cancelled = true
    }
  }, [currentUserId])

  if (!authReady) return null

  return (
    <HelmetProvider>
      <Toaster
        className="custom-toast"
        closeButton
        position="top-center"
        toastOptions={{
          className: "custom-toast",
          style: {
            borderRadius: "8px",
            boxShadow: "0 8px 16px rgba(145, 158, 171, 0.16)",
            color: "#212B36",
            display: "flex",
            gap: "1rem",
            padding: "12px",
          },
        }}
      />
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </HelmetProvider>
  )
}

export default App
